// Columna izquierda del explorer (`.col-filters` del mockup), en el mismo orden que él:
// Búsqueda · Financiera · Tipo · Producto · Monto · Fecha de venta. Producto, monto y el chip
// «Crédito» los aplica el front sobre lo cargado; el resto viaja al servidor.
//
// Los grupos Tienda y Vendedor del mockup no se dibujan: él mismo los deja en `display:none`
// porque esta pantalla es «Mis ventas» — una tienda y un vendedor, no hay nada que elegir.
import type { ReactNode, SelectHTMLAttributes } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { Input } from '@/kit';
import { cn } from '@/lib/cn';
import { mxDayOffset } from '@/lib/dateHelpers';
import {
    countActiveFilters,
    EMPTY_FILTERS,
    toggleInList,
    type MySalesFilters,
    type SaleTypeFilter,
} from '../lib/mySalesFilters';
import { FINANCIER_OPTIONS } from '../lib/mySalesFormat';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD = 'h-[26px] text-[11.5px]';
/** `.select` del mock: fondo blanco y chevron fino propio en vez de la flecha del navegador. */
const SELECT =
    'h-[26px] w-full appearance-none rounded-md border border-input bg-card pl-2 pr-[22px] text-[11.5px] outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50';
const CHIP =
    'min-w-0 flex-1 basis-0 cursor-pointer truncate whitespace-nowrap rounded-full border px-[3px] py-0.5 text-center text-[9px] font-semibold transition-colors';
const SHORTCUT =
    'h-6 cursor-pointer rounded border border-border bg-card px-1.5 text-center text-[12px] transition-colors hover:bg-accent';

/** Los dos chips de Tipo del mockup, repartidos en el renglón. */
const TYPE_CHIPS: { value: SaleTypeFilter; label: string }[] = [
    { value: 'contado', label: 'Contado' },
    { value: 'credito', label: 'Crédito' },
];

/** Rótulo de los filtros que el servidor no aplica, mientras queden páginas por traer. */
const LOADED_ONLY_HINT = 'sobre lo cargado';

const DATE_SHORTCUTS: { label: string; days: number }[] = [
    { label: 'Hoy', days: 0 },
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
];

export function MySalesFiltersPanel({
    filters,
    brands,
    onChange,
    collapsed,
    onToggleCollapsed,
    partialScope = false,
}: {
    filters: MySalesFilters;
    /** Marcas que hay en la tabla; el filtro ofrece lo que existe, no el catálogo entero. */
    brands: string[];
    onChange: (next: MySalesFilters) => void;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    /** Quedan páginas: los filtros que solo miran lo cargado lo dicen. */
    partialScope?: boolean;
}) {
    const patch = (partial: Partial<MySalesFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const activeCount = countActiveFilters(filters);

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeCount}
            onReset={() => {
                onChange(EMPTY_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                    />
                    <Input
                        type="search"
                        value={filters.query}
                        onChange={(e) => {
                            patch({ query: e.target.value });
                        }}
                        placeholder="Folio, cliente o CURP completa"
                        aria-label="Buscar venta por folio, cliente o CURP completa"
                        className={cn(FIELD, 'pl-6')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Financiera</div>
                <SelectField
                    value={filters.financier}
                    onChange={(e) => {
                        patch({ financier: e.target.value });
                    }}
                    aria-label="Filtrar por financiera"
                >
                    <option value="">Todas</option>
                    {FINANCIER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </SelectField>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Tipo</div>
                <div className="flex gap-[3px]">
                    {TYPE_CHIPS.map((chip) => {
                        const on = filters.types.includes(chip.value);
                        return (
                            <button
                                key={chip.value}
                                type="button"
                                aria-pressed={on}
                                title={
                                    partialScope && chip.value === 'credito'
                                        ? '«Crédito» filtra solo las ventas cargadas'
                                        : undefined
                                }
                                onClick={() => {
                                    patch({ types: toggleInList(filters.types, chip.value) });
                                }}
                                className={cn(
                                    CHIP,
                                    on
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-card hover:bg-accent',
                                )}
                            >
                                {chip.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={GROUP}>
                <FilterLabel partialScope={partialScope}>Producto</FilterLabel>
                <SelectField
                    value={filters.brand}
                    disabled={brands.length === 0}
                    onChange={(e) => {
                        patch({ brand: e.target.value });
                    }}
                    aria-label="Filtrar por marca del equipo"
                    title={
                        brands.length === 0
                            ? 'Ninguna de tus ventas guarda la marca del equipo'
                            : undefined
                    }
                >
                    <option value="">Todos</option>
                    {brands.map((brand) => (
                        <option key={brand} value={brand}>
                            {brand}
                        </option>
                    ))}
                </SelectField>
            </div>

            <div className={GROUP}>
                <FilterLabel partialScope={partialScope}>Monto</FilterLabel>
                <div className="grid grid-cols-2 gap-1">
                    <Input
                        type="number"
                        inputMode="numeric"
                        value={filters.minAmount}
                        onChange={(e) => {
                            patch({ minAmount: e.target.value });
                        }}
                        placeholder="Mín"
                        aria-label="Monto mínimo en pesos"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <Input
                        type="number"
                        inputMode="numeric"
                        value={filters.maxAmount}
                        onChange={(e) => {
                            patch({ maxAmount: e.target.value });
                        }}
                        placeholder="Máx"
                        aria-label="Monto máximo en pesos"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Fecha de venta</div>
                <div className="mb-1 grid grid-cols-2 gap-1">
                    <Input
                        type="date"
                        value={filters.from}
                        onChange={(e) => {
                            patch({ from: e.target.value });
                        }}
                        aria-label="Vendida desde"
                        className={FIELD}
                    />
                    <Input
                        type="date"
                        value={filters.to}
                        onChange={(e) => {
                            patch({ to: e.target.value });
                        }}
                        aria-label="Vendida hasta"
                        className={FIELD}
                    />
                </div>
                <div className="grid grid-cols-4 gap-1">
                    {DATE_SHORTCUTS.map((shortcut) => (
                        <button
                            key={shortcut.label}
                            type="button"
                            onClick={() => {
                                patch({ from: mxDayOffset(shortcut.days), to: mxDayOffset(0) });
                            }}
                            className={SHORTCUT}
                        >
                            {shortcut.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            patch({ from: '', to: '' });
                        }}
                        className={SHORTCUT}
                    >
                        Todo
                    </button>
                </div>
            </div>
        </FiltersColumn>
    );
}

/** Encabezado de un grupo que solo filtra lo cargado; con páginas pendientes lo rotula. */
function FilterLabel({ children, partialScope }: { children: ReactNode; partialScope: boolean }) {
    return (
        <div className={LABEL}>
            {children}
            {partialScope ? (
                <span className="ml-1 font-semibold normal-case tracking-normal">
                    · {LOADED_ONLY_HINT}
                </span>
            ) : null}
        </div>
    );
}

/** El `<select>` con el chevron del mock encima. */
function SelectField({
    children,
    ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
    return (
        <div className="relative">
            <select {...props} className={SELECT}>
                {children}
            </select>
            <ChevronDown
                className="pointer-events-none absolute right-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                aria-hidden
            />
        </div>
    );
}
